/**
 * Car-ball contact — the ordinary collision response plus the "Psyonix impulse".
 *
 * Implements §3 of docs/PHYSICS_REFERENCE.md (smish.dev ball_simulation_3) with
 * the corrections recorded in docs/CONSTANTS_SOURCES.md §10. Four things about
 * this model are counter-intuitive and all four are load-bearing:
 *
 *  1. TWO DIFFERENT NORMALS. The ordinary contact response uses the geometric
 *     contact normal off the car's hitbox (an OBB, so on a flat panel that
 *     normal is the panel's, regardless of where the ball is). The psyonix term
 *     does NOT: its direction is CENTRE-TO-CENTRE, `ball.position -
 *     car.position`, biased down and flattened toward the car's forward axis.
 *     That is why a ball resting on the roof of a car gets flicked forward.
 *
 *  2. IT IS A DELTA-VELOCITY, NOT AN IMPULSE. RocketSim and RLUtilities both add
 *     `hitDir * relSpeed * scale(relSpeed)` straight onto the ball's velocity —
 *     the ball mass is already divided out. The reference article writes it as
 *     `J = m * |dv| * s * n`, and transcribing that literally into a code path
 *     that then divides by m double-counts the mass and gives a term 30x too
 *     small. BALL_MASS deliberately does not appear anywhere in this file.
 *
 *  3. THE CAR GETS NO REACTION. Newton's third law is explicitly violated: the
 *     added velocity goes onto the ball and nothing at all goes onto the car.
 *     Momentum is not conserved and that is correct, not a bug to be fixed.
 *
 *  4. THE COOLDOWN IS PER CAR. RocketSim stores
 *     `tickCountWhenExtraImpulseApplied` inside each Car's BallHitInfo, not on
 *     the ball, so in a two-car pinch BOTH cars apply their full impulse on the
 *     same tick. The per-car state lives in `PsyonixImpulseState`, passed in by
 *     the caller — a module-level global would silently halve every pinch.
 *
 * The article itself notes the model "is not suitable for wheel hits or
 * pinches"; we implement it anyway because it is what the two simulators do.
 */

import type { BallState } from './ball';
import type { CarState } from './car-state';
import {
  BALL_FRICTION,
  BALL_MAX_ANGULAR_SPEED,
  BALL_SPIN_COUPLING,
  CARBALL_FRICTION,
  CARBALL_RESTITUTION,
  PSYONIX_IMPULSE_COOLDOWN_TICKS,
  PSYONIX_IMPULSE_FORWARD_SCALE,
  PSYONIX_IMPULSE_MAX_DELTA_VEL,
  PSYONIX_IMPULSE_SCALE_CURVE,
  PSYONIX_IMPULSE_Z_SCALE,
} from './constants';
import { DEFAULT_PHYSICS, type ResolvedPhysics } from './mutators';
import {
  addScaledTo,
  clampLengthBy,
  cross,
  dot,
  forward as forwardOf,
  length,
  normalize,
  piecewiseLinear,
  scale,
  sub,
  type Vec3,
  vec3,
} from './math';
import { carHitbox, type Contact, obbSphereContact } from './obb';

// ---------------------------------------------------------------------------
// Per-car impulse state
// ---------------------------------------------------------------------------

/**
 * The psyonix-impulse cooldown, held PER CAR (see note 4 above).
 *
 * `lastImpulseTick` is the tick index at which this car last applied its extra
 * impulse; -Infinity means "never". A car may fire again once at least
 * PSYONIX_IMPULSE_COOLDOWN_TICKS ticks have elapsed.
 */
export interface PsyonixImpulseState {
  lastImpulseTick: number;
}

export const makePsyonixImpulseState = (): PsyonixImpulseState => ({
  lastImpulseTick: Number.NEGATIVE_INFINITY,
});

/** Whether this car's extra impulse is off cooldown at `tick`. */
export const psyonixImpulseReady = (state: PsyonixImpulseState, tick: number): boolean =>
  tick - state.lastImpulseTick >= PSYONIX_IMPULSE_COOLDOWN_TICKS;

// ---------------------------------------------------------------------------
// The psyonix impulse
// ---------------------------------------------------------------------------

/**
 * s(|dv|) — the scale curve, piecewise linear with clamp-to-last at both ends.
 *
 * Clamp-to-last is RocketSim's behaviour and is deliberate: RLUtilities' table
 * is declared [6][2] with only 4 rows filled, so at |dv| == 4600.0 exactly it
 * falls out of its loop and returns -1.0, pointing the impulse INTO the car.
 */
export const psyonixScale = (relSpeed: number): number =>
  piecewiseLinear(PSYONIX_IMPULSE_SCALE_CURVE, relSpeed);

/** Relative speed after the MAXDELTAVEL clamp. Both the magnitude and the curve
 *  lookup use this clamped value, not the raw one. */
export const clampedRelativeSpeed = (relSpeed: number): number =>
  Math.min(Math.max(relSpeed, 0), PSYONIX_IMPULSE_MAX_DELTA_VEL);

/**
 * Speed added to the ball by the psyonix term, |dv| * s(|dv|).
 *
 * NOT monotonic. On [2300, 4600] the curve is s = 0.8 - dv/9200, so the added
 * speed is 0.8*dv - dv^2/9200 — a downward parabola peaking at dv = 3680 with
 * s = 0.40, i.e. 1472 uu/s. At the 4600 clamp you get only 1380 uu/s. Closing
 * on the ball faster than 3680 uu/s makes the hit WEAKER, which is the single
 * most surprising property of the whole model.
 */
export const psyonixAddedSpeed = (relSpeed: number): number => {
  const dv = clampedRelativeSpeed(relSpeed);
  return dv * psyonixScale(dv);
};

/**
 * The psyonix hit direction, verbatim from ball_simulation_3:
 *
 *   vec3 f = car.forward();
 *   vec3 n = ball.position - car.position;
 *   n[2] *= 0.35f;
 *   n = normalize(n - 0.35f * dot(n, f) * f);
 *
 * Note the order: the z-scale is applied FIRST and the dot product is taken
 * against the already-scaled vector. The z-scale flattens the direction toward
 * horizontal (a ball sitting on the roof is still driven mostly forwards); the
 * forward-scale removes 35 % of the car-forward component, which stops a
 * head-on hit from being purely along the nose. `f` need not be horizontal —
 * on a wall or upside down this rotates with the car.
 */
export const psyonixHitDirection = (ballPosition: Vec3, carPosition: Vec3, carForward: Vec3): Vec3 => {
  const n = sub(ballPosition, carPosition);
  n.z *= PSYONIX_IMPULSE_Z_SCALE;
  const alongForward = dot(n, carForward);
  return normalize(sub(n, scale(carForward, PSYONIX_IMPULSE_FORWARD_SCALE * alongForward)));
};

/**
 * The full psyonix delta-velocity for a car-ball pair, ignoring the cooldown.
 *
 * `dv = v_ball - v_car`; only its MAGNITUDE is used — the direction comes
 * entirely from the centre-to-centre geometry above. This is why a stationary
 * car struck by a fast ball and a fast car striking a stationary ball add the
 * same speed.
 */
export const psyonixDeltaVelocity = (ball: BallState, car: CarState): Vec3 => {
  const relSpeed = clampedRelativeSpeed(length(sub(ball.velocity, car.velocity)));
  const dir = psyonixHitDirection(ball.position, car.position, forwardOf(car.orientation));
  return scale(dir, relSpeed * psyonixScale(relSpeed));
};

// ---------------------------------------------------------------------------
// Geometry
// ---------------------------------------------------------------------------

/** Contact between the car's hitbox and the ball, or null when they miss.
 *  The box is the CAR BODY's (./car-config), so a Dominus presents a longer,
 *  flatter target than an Octane. The ball-size mutator moves the contact
 *  distance and nothing else here. */
export const carBallContact = (
  car: CarState,
  ball: BallState,
  physics: ResolvedPhysics = DEFAULT_PHYSICS
): Contact | null =>
  obbSphereContact(
    carHitbox(car.position, car.orientation, car.config),
    ball.position,
    physics.ballRadius
  );

// ---------------------------------------------------------------------------
// Ordinary contact response
// ---------------------------------------------------------------------------

/** Below this slip speed there is no slip to oppose, so no tangential impulse.
 *  Same guard as the world bounce; the reference expression divides by |s|. */
const SLIP_EPSILON = 1e-6;

/**
 * The ordinary (non-psyonix) collision response, evaluated in the CAR's rest
 * frame and applied to the ball.
 *
 * This is the same expression as `bounceDeltas` in ./ball, with two deliberate
 * differences:
 *   - restitution is CARBALL_RESTITUTION (0 — a perfectly inelastic contact),
 *     not the world's BALL_RESTITUTION of 0.6. Car-ball contact does not bounce;
 *     everything you see come off a car is the psyonix term.
 *   - the velocity fed in is RELATIVE to the car, so a ball resting on a moving
 *     car is dragged along instead of being scrubbed by friction.
 * ./ball is left untouched, which is why the expression is repeated rather than
 * parameterized.
 *
 * The car is treated as kinematic here: we do not push it back. The reaction on
 * the CAR from this ordinary term belongs to the car's own contact resolution
 * and is out of scope for this file. (The psyonix term, separately, has no
 * reaction in any implementation — see note 3 at the top.)
 */
export const carBallBounceDeltas = (
  relativeVelocity: Vec3,
  angularVelocity: Vec3,
  n: Vec3,
  physics: ResolvedPhysics = DEFAULT_PHYSICS
): { dv: Vec3; dw: Vec3 } => {
  const radius = physics.ballRadius;
  const vPerp = scale(n, dot(relativeVelocity, n));
  const vPara = sub(relativeVelocity, vPerp);
  const vSpin = scale(cross(n, angularVelocity), radius);
  const s = { x: vPara.x + vSpin.x, y: vPara.y + vSpin.y, z: vPara.z + vSpin.z };

  const sMag = length(s);

  const deltaVPerp = scale(vPerp, -(1 + CARBALL_RESTITUTION));

  let deltaVPara: Vec3;
  if (sMag < SLIP_EPSILON) {
    deltaVPara = vec3(0, 0, 0);
  } else {
    const ratio = length(vPerp) / sMag;
    deltaVPara = scale(s, -Math.min(1, CARBALL_FRICTION * ratio) * BALL_FRICTION);
  }

  return {
    dv: {
      x: deltaVPerp.x + deltaVPara.x,
      y: deltaVPerp.y + deltaVPara.y,
      z: deltaVPerp.z + deltaVPara.z,
    },
    // w += A * R * cross(delta_v_para, n) — argument order is load-bearing.
    dw: scale(cross(deltaVPara, n), BALL_SPIN_COUPLING * radius),
  };
};

// ---------------------------------------------------------------------------
// The full hit
// ---------------------------------------------------------------------------

export interface CarBallHitResult {
  /** Geometric contact used for the ordinary response and for depenetration. */
  contact: Contact;
  /** Ball velocity delta from the ordinary contact response. */
  ordinaryDeltaV: Vec3;
  /** Ball angular velocity delta from the ordinary contact response. */
  ordinaryDeltaW: Vec3;
  /** Ball velocity delta from the psyonix term; zero when it was on cooldown. */
  psyonixDeltaV: Vec3;
  /** False when this car's psyonix term was suppressed by its own cooldown. */
  psyonixApplied: boolean;
  /** |v_ball - v_car| BEFORE the hit, after the MAXDELTAVEL clamp. */
  relativeSpeed: number;
}

/**
 * Resolve one car-ball contact and apply it to the ball, in place.
 *
 * Returns null when the hitbox and the ball do not overlap. Nothing is written
 * to the car: see notes 3 and 4 at the top of this file.
 *
 * Order matters — the ordinary response first, then the psyonix term ON TOP of
 * it. Both are computed from the PRE-hit relative velocity so that the psyonix
 * magnitude does not depend on how much energy the inelastic contact just
 * removed.
 *
 * `tick` is the simulation tick index; `impulseState` is this car's own
 * cooldown record and is mutated when the impulse fires.
 */
export const applyCarBallHit = (
  car: CarState,
  ball: BallState,
  impulseState: PsyonixImpulseState,
  tick: number,
  physics: ResolvedPhysics = DEFAULT_PHYSICS
): CarBallHitResult | null => {
  const contact = carBallContact(car, ball, physics);
  if (!contact) return null;

  const relVel = sub(ball.velocity, car.velocity);
  const relativeSpeed = clampedRelativeSpeed(length(relVel));

  // Ordinary response, in the car's rest frame, about the OBB contact normal.
  const { dv, dw } = carBallBounceDeltas(relVel, ball.angularVelocity, contact.normal, physics);

  // Psyonix term, about the centre-to-centre direction. Computed from the
  // pre-hit state, gated by this car's own cooldown.
  const ready = psyonixImpulseReady(impulseState, tick);
  const psyonixDeltaV = ready ? psyonixDeltaVelocity(ball, car) : vec3(0, 0, 0);
  if (ready) impulseState.lastImpulseTick = tick;

  addScaledTo(ball.velocity, dv, 1);
  addScaledTo(ball.velocity, psyonixDeltaV, 1);
  addScaledTo(ball.angularVelocity, dw, 1);
  clampLengthBy(ball.angularVelocity, BALL_MAX_ANGULAR_SPEED);

  return {
    contact,
    ordinaryDeltaV: dv,
    ordinaryDeltaW: dw,
    psyonixDeltaV,
    psyonixApplied: ready,
    relativeSpeed,
  };
};
